// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The event log.
 *
 * A trust surface, not a debug aid. This tool invents pixels — that is what a
 * single displaced sheet does at a depth edge — so the least it can do is show
 * its working: which backend ran the model and whether the depth field passed
 * its sanity check, what the measured excursion cap came out at and in what
 * units, how many frames were drawn, which codec the browser agreed to and how
 * big the file was.
 *
 * It is also the only console this tool has. `console.*` is dropped at build
 * time, because the photographs flowing through here are family pictures and a
 * stray debug line would print pieces of one into the developer tools of a
 * machine that may not be theirs.
 *
 * The drawer covers most of a phone screen AND hides its own toggle button, so
 * it ships the same four exits the modals do, and it is created and removed
 * rather than hidden.
 */

import type { LogLevel } from './types.ts';

interface LogLine {
  time: string;
  level: LogLevel;
  message: string;
}

const MARKS: Record<LogLevel, string> = { good: '✓', info: '·', warn: '!', error: '×' };

const lines: LogLine[] = [];
let listEl: HTMLElement | null = null;
let countEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function render() {
  if (!listEl) return;
  if (lines.length === 0) {
    listEl.innerHTML =
      '<p class="log-empty">Nothing yet. Open a photograph and every step will appear here.</p>';
    return;
  }
  listEl.replaceChildren(
    ...lines.map((line) => {
      const row = document.createElement('div');
      row.className = 'log-line';
      const t = document.createElement('span');
      t.className = 'log-time';
      t.textContent = line.time;
      const m = document.createElement('span');
      m.className = `log-mark lv-${line.level}`;
      m.textContent = MARKS[line.level];
      const s = document.createElement('span');
      s.className = 'log-msg';
      s.textContent = line.message;
      row.append(t, m, s);
      return row;
    }),
  );
  listEl.scrollTop = listEl.scrollHeight;
}

export function logEvent(level: LogLevel, message: string) {
  lines.push({ time: stamp(), level, message });
  if (lines.length > 400) lines.splice(0, lines.length - 400);
  if (countEl) countEl.textContent = String(lines.length);
  render();
}

export function logText(): string {
  return lines.map((l) => `[${l.time}] ${MARKS[l.level]} ${l.message}`).join('\n');
}

export function closeDrawer() {
  if (!backdropEl) return;
  document.removeEventListener('keydown', onKey, true);
  backdropEl.remove();
  backdropEl = null;
  listEl = null;
  document.getElementById('log-toggle')?.setAttribute('aria-expanded', 'false');
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    closeDrawer();
  }
}

export function openDrawer() {
  if (backdropEl) return;
  const host = document.getElementById('modal-host');
  if (!host) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'drawer-backdrop';

  const drawer = document.createElement('aside');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.setAttribute('aria-label', 'Event log');

  const head = document.createElement('div');
  head.className = 'drawer-head';
  const title = document.createElement('h2');
  title.className = 'panel-title';
  title.textContent = 'Event log';

  const close = document.createElement('button');
  close.className = 'icon-btn';
  close.type = 'button';
  close.setAttribute('aria-label', 'Close the event log');
  close.innerHTML =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  head.append(title, close);

  const list = document.createElement('div');
  list.className = 'drawer-log';
  drawer.append(head, list);
  backdrop.append(drawer);

  backdrop.addEventListener('pointerdown', (event) => {
    if (!drawer.contains(event.target as Node)) closeDrawer();
  });
  close.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeDrawer();
  });
  close.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeDrawer();
    }
  });
  document.addEventListener('keydown', onKey, true);

  host.append(backdrop);
  backdropEl = backdrop;
  listEl = list;
  render();
  document.getElementById('log-toggle')?.setAttribute('aria-expanded', 'true');
  close.focus();
}

export function bindLogToggle(button: HTMLElement, counter: HTMLElement) {
  countEl = counter;
  countEl.textContent = String(lines.length);
  button.addEventListener('click', () => {
    if (backdropEl) closeDrawer();
    else openDrawer();
  });
}
